import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const DashboardContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The Tournaly home screen has been completely redesigned. Instead of a plain list of
      your tournaments, you now land on a smart dashboard that surfaces what actually needs
      your attention — right when you open the app.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      What's on the dashboard
    </Text>

    {[
      { title: 'Pending actions', desc: 'Unanswered tournament invitations, team invitations, and friend requests are shown at the top so nothing gets missed.' },
      { title: 'Active tournaments', desc: 'Your in-progress tournaments front and centre — tap to go straight to the bracket.' },
      { title: 'My leagues', desc: 'Your most recent leagues with live standings at a glance.' },
      { title: 'Recent activity', desc: 'Latest posts from players you follow — stay connected without opening the feed.' },
      { title: 'Discover', desc: 'A curated selection of public tournaments you might want to follow or join.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.title}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Your full tournament list is still there
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      "My Tournaments" is now a dedicated screen in the drawer with the complete, filterable
      list of all your tournaments — setup, in progress, and completed. The dashboard is
      your quick overview; the tournament list is your full archive.
    </Text>
  </View>
);

const DashboardContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La pantalla de inicio de Tournaly ha sido completamente rediseñada. En lugar de una
      lista simple de tus torneos, ahora aterrizas en un dashboard inteligente que muestra
      lo que realmente necesita tu atención — justo cuando abres la app.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Qué hay en el dashboard
    </Text>

    {[
      { title: 'Acciones pendientes', desc: 'Las invitaciones a torneos, invitaciones a equipos y solicitudes de amistad sin responder aparecen arriba para que nada se pierda.' },
      { title: 'Torneos activos', desc: 'Tus torneos en curso al frente y en el centro — toca para ir directamente al bracket.' },
      { title: 'Mis ligas', desc: 'Tus ligas más recientes con la clasificación en directo de un vistazo.' },
      { title: 'Actividad reciente', desc: 'Las últimas publicaciones de los jugadores que sigues — mantente conectado sin abrir el feed.' },
      { title: 'Descubrir', desc: 'Una selección curada de torneos públicos que quizás quieras seguir o unirte.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.title}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Tu lista completa de torneos sigue ahí
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      "Mis Torneos" es ahora una pantalla dedicada en el menú con la lista completa y
      filtrable de todos tus torneos — en preparación, en curso y completados. El dashboard
      es tu resumen rápido; la lista de torneos es tu archivo completo.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-03-home-dashboard',
  title: 'New Home Dashboard: Everything at a Glance',
  titleEs: 'Nuevo Dashboard de Inicio: Todo de un Vistazo',
  date: '2026-03-03',
  summary: "Tournaly's tournament management dashboard shows your pending invitations, active brackets, league standings, and social activity — everything you need to run your tournaments at a glance.",
  summaryEs: 'El panel de gestión de torneos de Tournaly muestra invitaciones pendientes, brackets activos, clasificaciones de liga y actividad social — todo lo que necesitas para gestionar tus torneos de un vistazo.',
  tags: ['UX', 'Feature'],
  Content: DashboardContent,
  ContentEs: DashboardContentEs,
};
